import { Injectable, inject } from '@angular/core';
import { BehaviorSubject, Observable, map, of } from 'rxjs';

import { BaseService } from './base-service';
import { enrichResult } from '../utils/errors';
import { GetBudget$Params } from '@api/backend-connector/fn/budgets/get-budget';
import { GetDetailedBudget$Params } from '@api/backend-connector/fn/budgets/get-detailed-budget';
import {
  BudgetDtoPage,
  BudgetDto,
  CreateBudgetRequest,
  UpdateBudgetRequest,
  BaseProjectDto,
} from '@api/backend-connector/models';
import { BudgetsApi } from '@api/backend-connector/services/budgets-api';

const toasts = {
  createSuccessSummary: 'i18n.BudgetToast.createSuccessSummary',
  createSuccessDetail: 'i18n.BudgetToast.createSuccessDetail',
  createFailSummary: 'i18n.BudgetToast.createFailSummary',
  createFailDetail: 'i18n.BudgetToast.createFailDetail',

  updateSuccessSummary: 'i18n.Toast.updateSuccessSummary',
  updateSuccessDetail: 'i18n.Toast.updateSuccessDetail',
  updateFailSummary: 'i18n.Toast.updateFailSummary',
  updateFailDetail: 'i18n.Toast.updateFailDetail',

  deleteSuccessSummary: 'i18n.BudgetToast.deleteSuccessSummary',
  deleteSuccessDetail: 'i18n.BudgetToast.deleteSuccessDetail',
  deleteFailSummary: 'i18n.BudgetToast.deleteFailSummary',
  deleteFailDetail: 'i18n.BudgetToast.deleteFailDetail',
};

export enum CostCategoryEnum {
  basic = 'basic',
  labor = 'labor',
}

export const BudgetColors = {
  estimates: '#808080' as const, // grey
  effectives: '#53b9cf' as const, // primary lightblue
};

export const BudgetLinesStyles = {
  estimates: {
    color: BudgetColors.estimates,
    width: 2,
  } as const,
  effectives: {
    color: BudgetColors.effectives,
    width: 4,
  } as const,
} as const;

@Injectable({
  providedIn: 'root',
})
export class BudgetsService extends BaseService {
  private readonly budgetsApi = inject(BudgetsApi);
  readonly toasts = toasts;

  readonly refresh$ = new BehaviorSubject<void>(undefined);

  listBudgets(
    params: Parameters<typeof BudgetsService.prototype.budgetsApi.listBudgets>[0],
  ): Observable<BudgetDtoPage> {
    return this.budgetsApi.listBudgets(params);
  }

  getBudget(params: GetBudget$Params) {
    return this.budgetsApi.getBudget(params);
  }

  getDetailedBudget(params: GetDetailedBudget$Params) {
    return this.budgetsApi.getDetailedBudget(params);
  }

  createBudget(request: CreateBudgetRequest) {
    return this.budgetsApi.createBudget({ body: request }).pipe(enrichResult());
  }

  updateBudget(id: string, request: UpdateBudgetRequest) {
    return this.budgetsApi.updateBudget({ id, body: request });
  }

  deleteBudget(params: { id: string }) {
    return this.budgetsApi.deleteBudget(params);
  }

  getDefaultBudget({ id, budgetsCount }: BaseProjectDto): Observable<BudgetDto | undefined> {
    return budgetsCount > 0
      ? this.listBudgets({ projectId: id, offset: 0, limit: 1 }).pipe(map(({ items }) => items[0]))
      : of(undefined);
  }
}
